// src/hooks/usePaymentGuard.js
import { useState, useCallback } from 'react';
import useBillingStore from '../services/useBillingStore';

/**
 * Hook para verificar se o usuário pode executar ações que requerem pagamento.
 *
 * Uso:
 * const { canExecute, guardAction, PaymentBlockModal } = usePaymentGuard();
 *
 * // Verificar antes de executar
 * if (canExecute) {
 *   // fazer ação
 * }
 *
 * // Ou usar guardAction para encapsular
 * const handleAddLead = guardAction(() => {
 *   // adicionar lead
 * });
 */
export const usePaymentGuard = () => {
  const { subscription } = useBillingStore();
  const [showBlockModal, setShowBlockModal] = useState(false);

  // Verificar se precisa de pagamento
  const needsPayment = subscription?.needs_payment ||
    ['pending_payment', 'trialing', 'past_due', 'canceled'].includes(subscription?.status);

  // Se não precisa de pagamento, pode executar
  const canExecute = !needsPayment;

  // Função para encapsular ações que requerem pagamento
  const guardAction = useCallback((action) => {
    return (...args) => {
      if (needsPayment) {
        setShowBlockModal(true);
        return;
      }
      return action(...args);
    };
  }, [needsPayment]);

  // Função para mostrar o modal de bloqueio
  const showPaymentRequired = useCallback(() => {
    setShowBlockModal(true);
  }, []);

  // Função para fechar o modal
  const closeBlockModal = useCallback(() => {
    setShowBlockModal(false);
  }, []);

  // Função para ir para o checkout
  const goToCheckout = useCallback(() => {
    if (subscription?.checkout_url) {
      window.open(subscription.checkout_url, '_blank');
    }
  }, [subscription?.checkout_url]);

  return {
    canExecute,
    needsPayment,
    guardAction,
    showPaymentRequired,
    showBlockModal,
    closeBlockModal,
    goToCheckout,
    checkoutUrl: subscription?.checkout_url,
  };
};

export default usePaymentGuard;
